import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import Panda from "../components/Panda";
import { CurveBottom } from "../components/Decor";
import { modules } from "../data/mock";
import { useProgress } from "../context/ProgressContext";
import { Slider } from "../components/ui/slider";

const ModulesPage = () => {
  const { moduleProgress, completed } = useProgress();
  const [age, setAge] = useState(10);

  const recommended = modules.find((m) => {
    const [lo, hi] = m.ages.split("-").map(Number);
    return age >= lo && age <= hi;
  }) || modules[modules.length - 1];

  return (
    <div data-testid="lessons-page">
      <section className="relative bg-[#63c79a] overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 pt-14 pb-28 text-center relative">
          <h1 className="text-white font-bold text-4xl sm:text-5xl" style={{ fontFamily: "Literata, serif" }}>Choose your adventure</h1>
          <p className="text-[#0d3d2b] text-lg mt-4 max-w-2xl mx-auto font-medium">
            Each lesson can stand alone. Start at your child's current age, or start earlier - every module builds on the one before it.
          </p>

          <div className="mt-10 bg-white rounded-3xl shadow-xl p-6 sm:p-8 max-w-2xl mx-auto text-left" data-testid="age-picker">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-[#0c4a6e] text-lg">How old is your child?</p>
              <span className="font-logo text-4xl text-[#e5356f]" data-testid="age-value">{age}</span>
            </div>
            <Slider value={[age]} min={5} max={24} step={1} onValueChange={(v) => setAge(v[0])} className="py-2" />
            <div className="flex justify-between text-xs text-gray-500 mt-1"><span>5</span><span>24</span></div>
            <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 rounded-2xl p-4" style={{ background: `${recommended.color}33` }}>
              <Panda size={70} accessory={recommended.pandaAccessory} scarf={recommended.pandaScarf} item={recommended.pandaItem} />
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs uppercase tracking-widest font-bold" style={{ color: recommended.dark }}>Recommended</p>
                <p className="font-bold text-[#0c4a6e] text-lg">{recommended.short} - {recommended.title}</p>
              </div>
              <Link to={`/lessons/${recommended.slug}`} className="btn-pill text-white" style={{ background: recommended.button }} data-testid="recommended-link">
                Go
              </Link>
            </div>
          </div>
        </div>
        <CurveBottom color="#ffffff" />
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="relative">
          <div className="absolute left-[38px] top-6 bottom-6 w-2 bg-[#e9f9fd] rounded-full hidden sm:block" />
          <div className="space-y-6">
            {modules.map((m) => {
              const p = moduleProgress(m.id);
              const quiz = completed[m.id]?.quiz;
              return (
                <Link
                  key={m.id}
                  to={`/lessons/${m.slug}`}
                  className="relative flex items-center gap-5 sm:gap-8 bg-white rounded-3xl p-4 sm:p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 border-2 border-transparent hover:border-[#e9f9fd]"
                  data-testid={`lesson-row-${m.id}`}
                >
                  <div className="relative flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center shadow-inner" style={{ background: m.color }}>
                    <span className="font-logo text-3xl text-white drop-shadow">{m.num}</span>
                    {p === 100 && (
                      <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#2e8b57] text-white flex items-center justify-center border-2 border-white"><Check size={14} /></span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: m.dark }}>Ages {m.ages}</p>
                    <h3 className="font-bold text-xl text-[#0c4a6e] leading-tight" style={{ fontFamily: "Literata, serif" }}>{m.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 truncate">{m.tagline}</p>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="h-2.5 flex-1 max-w-xs bg-[#e9f9fd] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${p}%`, background: m.button }} />
                      </div>
                      <span className="text-xs font-bold text-gray-500">{p}%</span>
                      {quiz !== undefined && <span className="text-xs font-bold text-[#2e8b57]">Quiz {quiz}/{m.quiz.length}</span>}
                    </div>
                  </div>
                  <div className="hidden sm:block">
                    <Panda size={80} accessory={m.pandaAccessory} scarf={m.pandaScarf} item={m.pandaItem} />
                  </div>
                  <ChevronRight className="text-[#1b8bc9] flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModulesPage;
